import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@moe/oss/ui/select";
import { Slider } from "@moe/oss/ui/slider";
import { Switch } from "@moe/oss/ui/switch";
import { TipBubble } from "@moe/priv/components/tip-bubble";
import { useDebounceFn } from "@moe/priv/hooks/use-debounce-fn";
import { Enums } from "@moe/priv/types/sb-types";
import { extractTags } from "@moe/priv/utils";
import { createFileRoute } from "@tanstack/react-router";
import { Navbar } from "@web/components/Navbar";
import { TagBox } from "@web/components/tag/TagBox";
import { TagInput } from "@web/components/tag/TagInput";
import { TagList } from "@web/components/tag/TagList";
import { useSettingsMutation } from "@web/hooks/use-settings-mutation";
import { useAppContext } from "@web/route-services/root/AppContext";
import { Sidepane } from "@web/route-services/settings/Sidepane";
import { motion } from "motion/react";
import { useEffect, useRef, useState } from "react";

export const Route = createFileRoute("/settings/preference")({
  component: PreferencePage
});

export const fontScaleQK = "fontScale";

function PreferencePage() {
  const { isDesktopScreen, settings } = useAppContext();
  const settingsMutation = useSettingsMutation({ showSuccessToast: false });
  const [tagsInput, setTagsInput] = useState<string>("");
  const tagContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!settings) return;
    const input = settings.blacklistedTags.map((t) => `#${t}`).join(" ");
    setTagsInput(input);
  }, [settings]);

  // Font scale state
  const [fontScale, setFontScale] = useState(() => {
    const cached = localStorage.getItem(fontScaleQK);
    if (cached) return parseFloat(cached);
    return 1;
  });

  const updateTags = useDebounceFn(async (str: string) => {
    if (!settings) return;
    const tags = extractTags(str);
    const uniqueTags = [...new Set(tags)];
    settingsMutation.mutate({ blacklistedTags: uniqueTags });
  }, 1000);

  return (
    <div className="scroll-secondary from-background-gradient via-background to-background text-tx-secondary mb-24 flex h-screen w-screen bg-gradient-to-b text-sm antialiased md:mb-0 md:overflow-hidden">
      <Navbar />
      <div style={{ scrollbarGutter: "stable" }} className="scroll-home-page flex w-full flex-row overflow-auto py-4">
        {isDesktopScreen && <Sidepane />}
        <div className="flex h-full w-full flex-col items-center space-y-5 px-3 md:justify-center md:px-8">
          <PreferenceCard>
            {settings && (
              <>
                <div className="flex flex-col space-y-6">
                  <div className="relative z-50 flex flex-col space-y-1" ref={tagContainerRef}>
                    <div className="flex items-center gap-1">
                      <h2 className="text-tx-primary text-lg font-medium">Blacklist</h2>
                      <TipBubble text="Show confirmation dialog when deleting or rewinding messages" />
                    </div>
                    <TagBox
                      mode="text"
                      value={tagsInput}
                      onChange={(str) => {
                        setTagsInput(str);
                        updateTags(str);
                      }}
                    >
                      <TagInput className="rounded" multiline rows={5} placeholder="#nsfw #ntr #dominant" />
                      <TagList containerRef={tagContainerRef} float={true} className="" />
                    </TagBox>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <label className="mb-1 block font-medium">Show NSFW</label>
                      <TipBubble text="Toggle visibility of NSFW (Not Safe For Work) content" />
                    </div>
                    <Switch
                      checked={settings.nsfwOK}
                      onCheckedChange={(isChecked) => {
                        settingsMutation.mutate({ nsfwOK: isChecked });
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <label className="mb-1 block font-medium">Confirm Delete</label>
                      <TipBubble text="Show confirmation dialog when deleting or rewinding messages" />
                    </div>
                    <Switch
                      checked={settings.confirmDelete}
                      onCheckedChange={(isChecked) => {
                        settingsMutation.mutate({ confirmDelete: isChecked });
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="block font-medium">Avatar Size</label>
                    <Select
                      value={settings.messageAvatarSize}
                      onValueChange={(value) => {
                        settingsMutation.mutate({ messageAvatarSize: value as Enums<"messageavatarsize"> });
                      }}
                    >
                      <SelectTrigger className="w-28 md:w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sm">Small</SelectItem>
                        <SelectItem value="md">Medium</SelectItem>
                        <SelectItem value="lg">Large</SelectItem>
                        <SelectItem value="xl">Extra Large</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="block font-medium">Avatar Shape</label>
                    <Select
                      value={settings.messageAvatarShape}
                      onValueChange={(value) => {
                        settingsMutation.mutate({ messageAvatarShape: value as Enums<"messageavatarshape"> });
                      }}
                    >
                      <SelectTrigger className="w-28 md:w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="circle">Circle</SelectItem>
                        <SelectItem value="square">Square</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block font-medium">Font Scale</label>
                      <label>{fontScale}x</label>
                    </div>
                    <Slider
                      defaultValue={[fontScale]}
                      max={1.4}
                      min={0.8}
                      step={0.04}
                      color="bg-accent"
                      className="w-28 md:w-32"
                      onValueChange={([value]) => {
                        setFontScale(value);
                        localStorage.setItem(fontScaleQK, value.toString());
                        document.documentElement.style.setProperty("--font-scale", value.toString());
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </PreferenceCard>
        </div>
      </div>
    </div>
  );
}

function PreferenceCard({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      className="text-tx-secondary mt-20 flex w-full max-w-md flex-col items-center md:mt-0 md:max-w-2xl"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.2,
        ease: [0, 0.71, 0.2, 1.01]
      }}
    >
      <h1 className="text-tx-primary pb-4 text-2xl font-bold tracking-wide">Preferences</h1>
      <div className="border-line bg-container-primary flex h-fit w-full flex-col space-y-6 rounded-2xl border px-8 py-6 md:w-[30rem]">
        {children}
      </div>
    </motion.div>
  );
}
